import { useTranslation } from 'react-i18next';
import { FLOOR_TYPES } from '@shared/constants/index.ts';
import {
  ROOM_CATEGORIES,
  TASK_CATEGORIES,
  TEMPLATE_JOB_TYPES,
  TEMPLATE_SEGMENTS,
  type RoomCategory,
  type TaskCategory,
  type TemplateTags,
} from '@shared/constants/template-taxonomy.ts';
import { Checkbox, Select } from './ui.tsx';

/**
 * Category, tags and favorite on a room type or task. The codes are stable;
 * the labels come from `libraryTaxonomy` so Spanish staff do not edit English
 * headings.
 */
export function TaxonomyFields({
  kind,
  categoryCode,
  tags,
  isFavorite,
  onChange,
}: {
  kind: 'room' | 'task';
  categoryCode: string;
  tags: TemplateTags;
  isFavorite: boolean;
  onChange: (patch: { categoryCode?: string; tags?: TemplateTags; isFavorite?: boolean }) => void;
}) {
  const { t } = useTranslation();
  const categories = kind === 'room' ? ROOM_CATEGORIES : TASK_CATEGORIES;

  function toggle<K extends keyof TemplateTags>(key: K, value: TemplateTags[K][number], checked: boolean) {
    const current = tags[key] as string[];
    const next = checked ? [...current, value] : current.filter((item) => item !== value);
    onChange({ tags: { ...tags, [key]: next } });
  }

  return (
    <div>
      <Select
        label={t('libraryTaxonomy.categoryLabel')}
        hint={t('libraryTaxonomy.categoryHint')}
        value={categoryCode}
        onChange={(event) => onChange({ categoryCode: event.target.value })}
        options={[
          { value: '', label: t('libraryTaxonomy.categoryUnset') },
          ...categories.map((code) => ({
            value: code,
            label: t(`libraryTaxonomy.${kind}.${code as RoomCategory & TaskCategory}`),
          })),
        ]}
      />
      <fieldset className="mb-4">
        <legend className="field-label">{t('libraryTaxonomy.tagsLabel')}</legend>
        <p className="field-hint mb-2">{t('libraryTaxonomy.tagsHint')}</p>
        <div className="flex flex-wrap gap-x-4">
          {TEMPLATE_SEGMENTS.map((segment) => (
            <Checkbox
              key={segment}
              label={t(`libraryTaxonomy.segment.${segment}`)}
              checked={tags.segments.includes(segment)}
              onChange={(event) => toggle('segments', segment, event.target.checked)}
            />
          ))}
          {TEMPLATE_JOB_TYPES.map((jobType) => (
            <Checkbox
              key={jobType}
              label={t(`libraryTaxonomy.jobType.${jobType}`)}
              checked={tags.jobTypes.includes(jobType)}
              onChange={(event) => toggle('jobTypes', jobType, event.target.checked)}
            />
          ))}
          {FLOOR_TYPES.map((floorType) => (
            <Checkbox
              key={floorType}
              label={t(`libraryTaxonomy.floorType.${floorType}`)}
              checked={tags.floorTypes.includes(floorType)}
              onChange={(event) => toggle('floorTypes', floorType, event.target.checked)}
            />
          ))}
        </div>
      </fieldset>
      <Checkbox
        label={t('libraryTaxonomy.favorite')}
        hint={t('libraryTaxonomy.favoriteHint')}
        checked={isFavorite}
        onChange={(event) => onChange({ isFavorite: event.target.checked })}
      />
    </div>
  );
}
